import { GridOptions, SortModelItem } from "ag-grid-community";
import { useRef, useEffect, useCallback, useState } from "react";
import {
  createServerSideDatasource,
  useAgGridOptions,
} from "@platform/design-system";

import { useFetchClients } from "./useFetchClients";

import type { ClientPageState } from "../pages/Clients/types";

interface UseClientsTableProps {
  brokerId?: string | number;
  search?: string;
  clientPageState: ClientPageState;
  sessionStorageKey: string;
  setStorageItem: (key: string, value: ClientPageState) => void;
  getStorageItem: (key: string) => ClientPageState | null;
}
interface UseClientsTableResponse extends GridOptions {
  total: number;
  isError: boolean;
  resetPage: () => void;
}

export const useClientsTable = ({
  brokerId,
  search,
  clientPageState,
  sessionStorageKey,
  setStorageItem,
  getStorageItem,
}: UseClientsTableProps): UseClientsTableResponse => {
  const initialSortModel = clientPageState?.sortModel;
  const page = clientPageState?.page;
  const pageSize = clientPageState?.rowsPerPage;

  const { resetPage, initialState, ...restOfOptions } = useAgGridOptions({
    sessionStorageKey,
    sort: initialSortModel?.[0]?.sort ?? "asc",
    colId: initialSortModel?.[0]?.colId ?? "name",
    page,
    pageSize,
  });

  const [prevSearch, setPrevSearch] = useState(search);
  useEffect(() => {
    if (search !== prevSearch) {
      setPrevSearch(search);
      resetPage();
    }
  }, [search, prevSearch, resetPage]);

  const updateStorage = useCallback(
    ({
      sortModel,
      search,
    }: {
      sortModel: SortModelItem[];
      search?: string;
    }) => {
      const current = getStorageItem(sessionStorageKey) ?? {};
      setStorageItem(sessionStorageKey, {
        ...current,
        searchValue: search?.trim(),
        sortModel,
      });
    },
    [getStorageItem, sessionStorageKey, setStorageItem],
  );

  const { fetchClientsPage, total, isError } = useFetchClients({
    search,
    brokerId,
    callbackFn: updateStorage,
  });

  const fetchRef = useRef(fetchClientsPage);
  fetchRef.current = fetchClientsPage;

  const datasourceRef = useRef(
    createServerSideDatasource((params) => fetchRef.current(params)),
  );
  const serverSideDatasource = datasourceRef.current;

  return {
    total,
    isError,
    initialState,
    serverSideDatasource,
    resetPage,
    ...restOfOptions,
  };
};
